// One-click engagement creation from a finding (FLOW-05). The backend creates or
// returns that finding's one engagement, with its context (finding, objective,
// scope) and first plan; this hook only asks for it and opens it.
import { useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { readOwner } from '../authorization/model.ts';
import { api } from '../onboarding/api.ts';
import { useSession } from '../../state/session.ts';

export function useCreateEngagement() {
  const navigate = useNavigate();
  const workspace = useSession((s) => s.workspace);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<{ findingId: string; message: string } | null>(null);
  // One key per finding per page visit, so a repeated click returns the same engagement.
  const keys = useRef(new Map<string, string>());

  const create = async (findingId: string) => {
    if (!workspace || pendingId) return;
    const key = keys.current.get(findingId) ?? `investigate-${crypto.randomUUID()}`;
    keys.current.set(findingId, key);
    setPendingId(findingId);
    setError(null);
    try {
      const { engagementId } = await api.investigateFinding(
        workspace.id,
        findingId,
        key,
        workspace.setupSessionId ?? '',
        workspace.authorizationId ? readOwner(workspace.authorizationId) : '',
      );
      void navigate({ to: '/engagements/$engagementId', params: { engagementId } });
    } catch (err) {
      setError({
        findingId,
        message:
          err instanceof Error ? err.message : 'The engagement could not be created. Try again.',
      });
      setPendingId(null);
    }
  };

  return { create, pendingId, error };
}
